import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Lock, Unlock, BookCheck, Trash2, User, KeyRound, LogIn, RefreshCcw, Shield,
} from "lucide-react";
import { formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  "month.lock":           { label: "Locked month",         icon: Lock,       tone: "amber"   },
  "month.unlock":         { label: "Unlocked month",       icon: Unlock,     tone: "zinc"    },
  "audit_jv.create":      { label: "Posted Audit JV",      icon: BookCheck,  tone: "emerald" },
  "audit_jv.delete":      { label: "Deleted Audit JV",     icon: Trash2,     tone: "rose"    },
  "user.create":          { label: "Created user",         icon: User,       tone: "emerald" },
  "user.update":          { label: "Updated user",         icon: User,       tone: "zinc"    },
  "user.delete":          { label: "Deleted user",         icon: Trash2,     tone: "rose"    },
  "user.role_change":     { label: "Changed role",         icon: Shield,     tone: "amber"   },
  "user.password_reset":  { label: "Reset password",       icon: KeyRound,   tone: "amber"   },
  "auth.password_change": { label: "Changed own password", icon: KeyRound,   tone: "zinc"    },
  "auth.login_success":   { label: "Signed in",            icon: LogIn,      tone: "zinc"    },
};

const TONE_CLASSES = {
  amber:   "bg-amber-500/15 text-amber-700 border-amber-300",
  emerald: "bg-emerald-500/15 text-emerald-700 border-emerald-300",
  rose:    "bg-rose-500/15 text-rose-700 border-rose-300",
  zinc:    "bg-zinc-200 text-zinc-700 border-zinc-300",
};

function formatTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
}

function detailPreview(details) {
  if (!details) return "";
  const keys = Object.keys(details);
  if (!keys.length) return "";
  return keys.map((k) => `${k}: ${typeof details[k] === "object" ? JSON.stringify(details[k]) : details[k]}`).join(" · ");
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [filters, setFilters] = useState({
    event_type: "__all__",
    actor_email: "",
    from_date: "",
    to_date: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.event_type && filters.event_type !== "__all__") params.event_type = filters.event_type;
      if (filters.actor_email) params.actor_email = filters.actor_email.trim().toLowerCase();
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      const [rRes, tRes] = await Promise.all([
        axios.get(`${API}/audit-log`, { params }),
        axios.get(`${API}/audit-log/event-types`),
      ]);
      setRows(rRes.data || []);
      setEventTypes(tRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.event_type] = (m[r.event_type] || 0) + 1;
    return m;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-[Chivo] font-semibold tracking-tight">Audit Trail</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Chronological feed of administrative actions — month locks, Audit JVs, user and role changes, password resets, and logins.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="audit-refresh-btn">
          <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">Event type</Label>
              <Select value={filters.event_type} onValueChange={(v) => setFilters({ ...filters, event_type: v })}>
                <SelectTrigger data-testid="audit-filter-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All events</SelectItem>
                  {eventTypes.map((e) => (
                    <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">Actor email</Label>
              <Input value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} placeholder="user@..." data-testid="audit-filter-actor" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">From date</Label>
              <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="audit-filter-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">To date</Label>
              <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="audit-filter-to" />
            </div>
            <div className="flex items-end">
              <Button onClick={load} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="audit-apply-btn">
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Events ({rows.length})</span>
            <div className="flex flex-wrap gap-1">
              {Object.entries(counts).slice(0, 6).map(([k, n]) => {
                const meta = EVENT_META[k] || { label: k, tone: "zinc" };
                return (
                  <Badge key={k} variant="outline" className={TONE_CLASSES[meta.tone]}>
                    {meta.label}: {n}
                  </Badge>
                );
              })}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead className="w-[160px]">Event</TableHead>
                <TableHead className="w-[200px]">Actor</TableHead>
                <TableHead className="w-[220px]">Target</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">No events match the current filters.</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => {
                const meta = EVENT_META[r.event_type] || { label: r.event_type, tone: "zinc", icon: BookCheck };
                const Icon = meta.icon;
                return (
                  <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                    <TableCell className="font-[IBM_Plex_Mono] text-xs text-zinc-600 tabular-nums">
                      {formatTs(r.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${TONE_CLASSES[meta.tone]}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-zinc-800">{r.actor_email || "—"}</div>
                      {r.actor_role && (
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{r.actor_role}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-800">{r.target_label || r.target_id || "—"}</TableCell>
                    <TableCell className="text-xs text-zinc-500 truncate max-w-[380px]">{detailPreview(r.details)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
